import { useCallback } from 'react';
import { Modal } from '@tanium/react-modal';
import { Stack } from '@tanium/react-stack';
import { Form, Formik } from 'formik';
import { Button } from '@tanium/react-button';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { AwsRoleTargetAccess } from 'gql/AwsRoleTargetAccess';
import Helper from 'utils/Helper';
import FormTextArea from '../form/formTextArea';
import FormTextField from '../form/formTextField';

type AwsRoleTargetAccessForm = {
  adGroups: string;
  awsRoleName: string;
  awsSessionPolicy: string;
  description: string;
};

const defaultCreateForm: AwsRoleTargetAccessForm = {
  adGroups: '',
  awsRoleName: '',
  awsSessionPolicy: '',
  description: '',
};

function mapAwsRoleTargetAccessToForm({
  adGroups,
  awsRoleName,
  awsSessionPolicy,
  description,
}: AwsRoleTargetAccess): AwsRoleTargetAccessForm {
  return {
    adGroups: adGroups.join(','),
    awsRoleName,
    awsSessionPolicy,
    description: description || '',
  };
}

export type Operation =
  | {
      type: 'create';
      adGroups: string[];
      awsRoleName: string;
      awsSessionPolicy: string;
      description?: string;
      targetOrTargetGroupId: string;
    }
  | {
      type: 'update';
      adGroups: string[];
      awsRoleName: string;
      awsSessionPolicy: string;
      description?: string;
      id: string;
      targetOrTargetGroupId: string;
    }
  | {
      type: 'cancel';
    };

type AwsRoleTargetAccessCRUDModalProps = {
  isOpen: boolean;
  onProcess: (operation: Operation) => void;
  awsRoleToEdit?: AwsRoleTargetAccess;
  targetOrTargetGroupId: string;
};

const AwsRoleTargetAccessCRUDModal: React.VFC<AwsRoleTargetAccessCRUDModalProps> = ({
  onProcess,
  awsRoleToEdit,
  targetOrTargetGroupId,
  isOpen,
}) => {
  const { t } = useTranslation();

  const handleSubmit = useCallback(
    (data: AwsRoleTargetAccessForm) => {
      const operation: Operation =
        awsRoleToEdit === undefined
          ? {
              type: 'create',
              adGroups: Helper.safeConvertOptionalStringToArray(data.adGroups),
              awsRoleName: data.awsRoleName,
              awsSessionPolicy: data.awsSessionPolicy,
              description: data.description,
              targetOrTargetGroupId,
            }
          : {
              type: 'update',
              adGroups: Helper.safeConvertOptionalStringToArray(data.adGroups),
              awsRoleName: data.awsRoleName,
              awsSessionPolicy: data.awsSessionPolicy,
              description: data.description,
              id: awsRoleToEdit.id,
              targetOrTargetGroupId: awsRoleToEdit.targetOrTargetGroupID,
            };
      onProcess(operation);
    },
    [onProcess, awsRoleToEdit, targetOrTargetGroupId]
  );

  const handleCancel = useCallback(() => {
    onProcess({
      type: 'cancel',
    });
  }, [onProcess]);

  const validationSchema = yup.object().shape({
    awsRoleName: yup.string().label(t('AWS_ROLE_TARGET_ACCESS_ROLE_NAME_LABEL')).required(),
    description: yup.string().label(t('DESCRIPTION')).nullable(),
    adGroups: yup.string().label(t('AD_GROUPS')).required(),
    awsSessionPolicy: yup.string().label(t('AWS_ROLE_TARGET_ACCESS_SESSION_POLICY_LABEL')).nullable(),
  });

  return (
    <Modal
      isOpen={isOpen}
      size="md"
      onRequestClose={handleCancel}
      header={
        awsRoleToEdit === undefined
          ? t('AWS_ROLE_TARGET_ACCESS_CREATE_MODAL_HEADER')
          : t('AWS_ROLE_TARGET_ACCESS_UPDATE_MODAL_HEADER')
      }
    >
      <Formik
        initialValues={awsRoleToEdit !== undefined ? mapAwsRoleTargetAccessToForm(awsRoleToEdit) : defaultCreateForm}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <Form>
          <Stack direction="column" itemSpacing={1}>
            <FormTextField
              label={t('AWS_ROLE_TARGET_ACCESS_ROLE_NAME_LABEL')}
              description={t('AWS_ROLE_TARGET_ACCESS_ROLE_NAME_DESCRIPTION')}
              fieldName="awsRoleName"
              placeholder={t('AWS_ROLE_TARGET_ACCESS_ROLE_NAME_PLACEHOLDER')}
              required
            />
            <FormTextField
              label={t('DESCRIPTION')}
              description={t('AWS_ROLE_TARGET_ACCESS_DESCRIPTION_DESCRIPTION')}
              fieldName="description"
              placeholder={t('AWS_ROLE_TARGET_ACCESS_DESCRIPTION_PLACEHOLDER')}
            />
            <FormTextField
              label={t('AD_GROUPS')}
              description={t('AD_GROUPS_DESCRIPTION')}
              fieldName="adGroups"
              placeholder={t('AD_GROUPS_PLACEHOLDER')}
              required
            />
            <FormTextArea
              label={t('AWS_ROLE_TARGET_ACCESS_SESSION_POLICY_LABEL')}
              description={t('AWS_ROLE_TARGET_ACCESS_SESSION_POLICY_DESCRIPTION')}
              fieldName="awsSessionPolicy"
              placeholder={t('AWS_ROLE_TARGET_ACCESS_SESSION_POLICY_PLACEHOLDER')}
            />
            <Stack itemSpacing={1}>
              <Button data-testid="submit" type="submit">
                {awsRoleToEdit === undefined ? t('CREATE') : t('UPDATE')}
              </Button>
              <Button type="button" data-testid="cancel" variant="secondary" onClick={handleCancel}>
                {t('CANCEL')}
              </Button>
            </Stack>
          </Stack>
        </Form>
      </Formik>
    </Modal>
  );
};

export default AwsRoleTargetAccessCRUDModal;
